// src/pages/NewCarListingPage.tsx

import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Grid, Stack, CircularProgress, Pagination, Divider
} from '@mui/material';
import NewCarCard from '../components/NewCarCard';
import NewCarFilterBar from '../components/NewCarFilterBar';
import axiosInstance from '../services/axiosInstance';
import { decodeId } from '../utils/idEncoder';

const NewCarListingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // Parse URL Parameters mapped to Backend Input Model
  const parseIds = (key: string): number[] => {
    const raw = searchParams.get(key);
    if (!raw) return [];
    return raw.split(',').map(hash => decodeId(hash)).filter((id): id is number => id !== null);
  };

  const vehicleBrandIds = parseIds('vehicleBrandIds');
  const vehicleBodyTypeIds = parseIds('vehicleBodyTypeIds');
  const vehicleFuelTypeIds = parseIds('vehicleFuelTypeIds');

  // We decode DealerID if navigating from a specific Dealer profile to see all their new cars
  const dealerIdParam = searchParams.get('dealerId');
  const dealerId = dealerIdParam ? decodeId(dealerIdParam) : null;

  const minPrice = searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : null;
  const maxPrice = searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : null;
  const searchKeyword = searchParams.get('searchKeyword') || '';

  // State
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [vehicleList, setVehicleList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(searchKeyword);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    setSearchInput(searchKeyword);
  }, [searchKeyword]);

  // Fetch New Vehicles API
  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      try {
        const payload = {
          vehicleBrandIds: vehicleBrandIds.length > 0 ? vehicleBrandIds : null,
          vehicleBodyTypeIds: vehicleBodyTypeIds.length > 0 ? vehicleBodyTypeIds : null,
          vehicleFuelTypeIds: vehicleFuelTypeIds.length > 0 ? vehicleFuelTypeIds : null,
          minPrice,
          maxPrice,
          dealerId,
          searchKeyword,
          pageNumber: currentPage,
          pageSize: itemsPerPage,
        };

        const response = await axiosInstance.post("/CustomerVehicle/NewVehicleGetListPublished", payload);
        const result = response.data as any;

        if (result.responseCode === "00" && result.responseData) {
          const data = result.responseData;
          setVehicleList(data.vehicleList || []);
          setTotalRecords(data.totalRecords || 0);
          setTotalPages(data.totalPages || 0);
        } else {
          setVehicleList([]);
          setTotalRecords(0);
          setTotalPages(0);
        }
      } catch (err) {
        console.error("Error fetching new vehicles", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [location.search, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchKeyword, viewMode, location.search]);

  const handleFilterChange = (key: string, value: string) => {
    const currentParams = new URLSearchParams(location.search);
    if (value) currentParams.set(key, value);
    else currentParams.delete(key);
    currentParams.delete('page');
    navigate(`/new-vehicles?${currentParams.toString()}`);
  };

  const indexOfFirstRecord = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <>
      <Header />
      <Box sx={{ px: 4, py: 6, mt: 5, minHeight: '80vh', bgcolor: '#fafafa' }}>
        <Box display="flex" justifyContent="center">
          <Box sx={{ width: '100%', maxWidth: 1200 }}>

            {/* Top Bar Container */}
            <Box
              sx={{
                p: { xs: 2.5, md: 3.5 },
                mb: 4,
                borderRadius: 4,
                bgcolor: 'background.paper',
                boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
                border: '1px solid rgba(0,0,0,0.02)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, background: 'radial-gradient(circle, rgba(0,102,255,0.05) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

              <NewCarFilterBar
                viewMode={viewMode}
                onToggleViewMode={() => setViewMode(prev => (prev === 'grid' ? 'list' : 'grid'))}
                searchQuery={searchInput}
                onSearchChange={setSearchInput}
                onFilterChange={handleFilterChange}
                url="/new-vehicles"
              />

              <Divider sx={{ my: 3, borderStyle: 'dashed', borderColor: '#e0e0e0' }} />

              <Typography color="text.primary" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
                {totalRecords} {totalRecords === 1 ? 'New Model' : 'New Models'} Available
              </Typography>
            </Box>

            {/* Data Render */}
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {viewMode === 'grid' ? (
                  <Grid container spacing={2}>
                    {vehicleList.map((vehicle) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={vehicle.newVehicleId}>
                        <NewCarCard vehicle={vehicle} viewMode="grid" />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Stack spacing={2}>
                    {vehicleList.map(vehicle => (
                      <NewCarCard key={vehicle.newVehicleId} vehicle={vehicle} viewMode="list" />
                    ))}
                  </Stack>
                )}

                {/* Pagination */}
                {vehicleList.length > 0 ? (
                  <Box mt={5} display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="body2" color="text.secondary" mb={1.5} fontWeight={500}>
                      Showing {indexOfFirstRecord} to {indexOfLastRecord} of {totalRecords}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      color="primary"
                      shape="rounded"
                    />
                  </Box>
                ) : (
                  <Box mt={6} textAlign="center" p={4} bgcolor="#fff" borderRadius={4} border="1px dashed #ccc">
                    <Typography variant="h6" color="text.secondary" fontWeight={600}>No new vehicles found matching your criteria.</Typography>
                    <Typography variant="body2" color="text.disabled" mt={1}>Try adjusting your filters or search terms.</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default NewCarListingPage;